/* eslint-disable max-lines -- the rule was disabled before a comment became required */
import { type PaginationRenderState } from 'instantsearch.js/es/connectors/pagination/connectPagination'

import {
  AlgoliaPaginationRenderType,
  type PaginationItem,
} from './ProductSearchPaginationItem'

const START_PAGES_TO_SHOW = 3
const ALWAYS_SHOW_PAGES_IF_THERE_ARE_FEW_PAGES = 4
const JUMP_THREE_PAGES = 3
const JUMP_TWO_PAGES = 2

/*
  We build the first pagination item from the Array that will be rendered as clickable Pagination buttons in the UI.
  The first page is 1, but we need to use 0 as Algolia starts counting the pages from 0
 */
const buildFirstPaginationItem = (
  createAlgoliaUrl: (page: number) => string,
): PaginationItem => {
  return {
    pageNumber: 0,
    renderType: AlgoliaPaginationRenderType.NUMBER,
    url: createAlgoliaUrl(0),
  }
}

/*
  This function builds the last Pagination Item that has a number.
  Algolia starts counting pages from 0 therefore we must subtract 1 to get to the final page.
  E.g. We have 34 pages, this means 0, 1, 2, ..., 33
  To get to the last page we must do nbPages - 1 / 34 - 1 => 33
 */
const buildLastPaginationItem = (
  nbPages: number,
  createAlgoliaUrl: (page: number) => string,
): PaginationItem => {
  return {
    pageNumber: nbPages - 1,
    renderType: AlgoliaPaginationRenderType.NUMBER,
    url: createAlgoliaUrl(nbPages - 1),
  }
}

/*
  We build the current active pagination item first.
  We will use this as our starting point for the rest of the pages from the left and right side of this page.
  E.g. We have 24 pages. Current active page is 5, this means we build all necessary pagination items from the left side: <, 1, ...,
  After wards we build all right side pagination items: ..., 24, >
  The final result in the parent function will be: <, 1, ..., 5, ..., 24, >
 */
const buildActivePagePaginationItem = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
): PaginationItem => {
  return {
    pageNumber: currentActivePage,
    renderType: AlgoliaPaginationRenderType.CURRENT_ACTIVE_PAGE,
    url: createAlgoliaUrl(currentActivePage),
  }
}

// We build the left side "dots" Pagination Item and append it in the parent function to the array of Pagination Items.
const buildLeftDotsPaginationItem = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
  totalPages: number,
): PaginationItem[] => {
  /*
    If the current page is the last page in the Pagination this means we will have the last 3 pages available also.
    This scenario forces us to allocate to the "dots" pagination the 4th page from the end.
    E.g. We have 34 pages, active page is 33, the pagination should look like this: <, 1, ..., 31, 32, 33
    In this case the "..." will have to point to page 30.
   */
  if (currentActivePage === totalPages - 1) {
    return [
      {
        pageNumber: currentActivePage - JUMP_THREE_PAGES,
        renderType: AlgoliaPaginationRenderType.DOTS,
        url: createAlgoliaUrl(currentActivePage - JUMP_THREE_PAGES),
      },
    ]
  }

  /*
    This scenario has a similar behaviour as the previous one, but we handle the case if the current selected page is 1st from last page.
    E.g. We have 34 pages, active page is page 32, the pagination should look like this: <, 1, ..., 31, 32, 33. In this case the "..." will have to point to page 30.
    To get to page 30 from page 32 we need to do currentRefinement - 2.
   */
  if (currentActivePage + 1 === totalPages - 1) {
    return [
      {
        pageNumber: currentActivePage - JUMP_TWO_PAGES,
        renderType: AlgoliaPaginationRenderType.DOTS,
        url: createAlgoliaUrl(currentActivePage - JUMP_TWO_PAGES),
      },
    ]
  }

  if (currentActivePage <= START_PAGES_TO_SHOW - 1) {
    return []
  }

  return [
    {
      pageNumber: currentActivePage - 1,
      renderType: AlgoliaPaginationRenderType.DOTS,
      url: createAlgoliaUrl(currentActivePage - 1),
    },
  ]
}

// We build the right side "dots" Pagination Item and append it in the parent function to the array of Pagination Items.
const buildRightDotsPaginationItem = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
  totalPages: number,
): PaginationItem[] => {
  /*
    We know that if the current selected page is below 4 (In Algolia below 3 because it counts the pages from 0, 1, 2, 3...).
    In this case the "dots" will point towards page 4, since we will display 1, 2, 3, ...
   */
  if (currentActivePage < START_PAGES_TO_SHOW) {
    return [
      {
        pageNumber: JUMP_THREE_PAGES,
        renderType: AlgoliaPaginationRenderType.DOTS,
        url: createAlgoliaUrl(JUMP_THREE_PAGES),
      },
    ]
  }

  /*
    Here we also account for the "dots" located towards the end of the pagination.
    E.g. If the current page is 30, and we have 34 pages the pagination would look like this: <, 1, ..., 30, ..., 34, >
    Therefore we will assign the page "31" or currentRefinement + 1 to the "dots" pagination item.
   */
  if (currentActivePage + JUMP_TWO_PAGES < totalPages - 1) {
    return [
      {
        pageNumber: currentActivePage + 1,
        renderType: AlgoliaPaginationRenderType.DOTS,
        url: createAlgoliaUrl(currentActivePage + 1),
      },
    ]
  }

  /*
    We return an empty array so we can destructure in the parent function and not worry if it's empty or
    if it has a value from any of the 2 conditions from above.
   */
  return []
}

// We build the Previous Button (<) Pagination Item and append it in the parent function to the array of Pagination Items.
const buildLeftArrowPaginationItem = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
): PaginationItem => {
  return {
    pageNumber: currentActivePage - 1,
    renderType: AlgoliaPaginationRenderType.PREVIOUS_PAGE,
    url: createAlgoliaUrl(currentActivePage - 1),
  }
}

// We build the Next Button (>) Pagination Item and append it in the parent function to the array of Pagination Items.
const buildRightArrowPaginationItem = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
): PaginationItem => {
  return {
    pageNumber: currentActivePage + 1,
    renderType: AlgoliaPaginationRenderType.NEXT_PAGE,
    url: createAlgoliaUrl(currentActivePage + 1),
  }
}

/*
  When there are equal or fewer than 4 pages then we need to show all of them.
  We start creating the right side pages from the Anchor Page Item + 1
 */
const buildAllRightSidePages = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
  totalPages: number,
): PaginationItem[] => {
  const rightSidePaginationItems = []
  for (let page = currentActivePage + 1; page < totalPages; page += 1) {
    const paginationItem = {
      pageNumber: page,
      renderType: AlgoliaPaginationRenderType.NUMBER,
      url: createAlgoliaUrl(page),
    }

    rightSidePaginationItems.push(paginationItem)
  }

  // We add the Next Page item, >
  rightSidePaginationItems.push(
    buildRightArrowPaginationItem(currentActivePage, createAlgoliaUrl),
  )

  return rightSidePaginationItems
}

/*
  This function is building the pagination items that always have to be visible in the pagination array.
  There are 2 cases:
    1st, when the current active page is within the first 3 pages
    2nd, when the current active page is 2 or fewer pages away from the last page
  Both of these cases are explained below with comments.
 */
const buildAlwaysVisibleRightPageItems = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
  totalPages: number,
): PaginationItem[] => {
  const alwaysVisiblePages = []

  /*
    This case handles the situation when current Anchor Page is somewhere between 1 and 3.
    In this scenario we must show pages 1, 2, 3. E.g. We have 34 pages, current selected page is 1.
    In this scenario we must show 1, 2, 3, ... , 34, >
   */
  if (currentActivePage < START_PAGES_TO_SHOW) {
    for (
      let page = currentActivePage + 1;
      page < START_PAGES_TO_SHOW;
      page += 1
    ) {
      const paginationItem = {
        pageNumber: page,
        renderType: AlgoliaPaginationRenderType.NUMBER,
        url: createAlgoliaUrl(page),
      }
      alwaysVisiblePages.push(paginationItem)
    }

    return alwaysVisiblePages
  }

  /*
    This condition handles the situation when we are with the selected page towards the end of the pagination.
    E.g. We have 34 pages, current selected page is 32.
    In this scenario we must show <, 1, ..., 32, 33, 34, >
   */
  if (currentActivePage + JUMP_TWO_PAGES === totalPages - 1) {
    const paginationItem = {
      pageNumber: currentActivePage + 1,
      renderType: AlgoliaPaginationRenderType.NUMBER,
      url: createAlgoliaUrl(currentActivePage + 1),
    }

    alwaysVisiblePages.push(paginationItem)
  }

  return alwaysVisiblePages
}

/*
  We build all the pagination items that are on the right side of the Current Active Page.
  This includes "...", numbers and ">"
  E.g. We have 25 pages, current active page is 6. This means we build: ..., 25, >
 */
const buildRightSidePaginationItems = (
  algoliaPagination: PaginationRenderState,
): PaginationItem[] => {
  const {
    createURL: createAlgoliaUrl,
    currentRefinement: currentActivePage,
    nbPages: totalPages,
  } = algoliaPagination

  if (totalPages === 1) {
    return []
  }

  /*
    If the Algolia query returns 4 or less than 4, then we need to display all existing pages.
    E.g. Algolia returns 4 pages, we will show <*, 1, 2, 3, 4, >*
    * - this is optional based on what current active page we are on
   */
  if (totalPages <= ALWAYS_SHOW_PAGES_IF_THERE_ARE_FEW_PAGES) {
    return buildAllRightSidePages(
      currentActivePage,
      createAlgoliaUrl,
      totalPages,
    )
  }

  /*
    If the current selected page is Page 1 or Page 2 then we must show Page 1, Page 2, Page 3.
    Since we are in the buildRightSidePaginationItems we build only from current selected page + 1 until we reach Page 3
   */
  const alwaysVisiblePageItems = buildAlwaysVisibleRightPageItems(
    currentActivePage,
    createAlgoliaUrl,
    totalPages,
  )

  return [
    ...alwaysVisiblePageItems,
    ...buildRightDotsPaginationItem(
      currentActivePage,
      createAlgoliaUrl,
      totalPages,
    ),
    buildLastPaginationItem(totalPages, createAlgoliaUrl),
    buildRightArrowPaginationItem(currentActivePage, createAlgoliaUrl),
  ]
}

/*
  This function handles another specific scenario where if the user is on the 2nd form last page, then we have to show at least the last 3 pages.
  E.g. We have 33 pages, the user is on page 32.
  Since we are building on the left side of the Current Active Page, the pagination has to be built like this:
  [remaining pagination build in parent function], 31, 32, 33
  E.g. We have 33 pages, the user is on page 33. The pagination will look like:
  [remaining pagination build in parent function], 31, 32, 33
 */
const buildAlwaysVisibleLeftSidePageItems = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
  totalPages: number,
): PaginationItem[] => {
  const alwaysVisiblePages = []

  if (currentActivePage === START_PAGES_TO_SHOW - 1) {
    alwaysVisiblePages.push({
      pageNumber: currentActivePage - 1,
      renderType: AlgoliaPaginationRenderType.NUMBER,
      url: createAlgoliaUrl(currentActivePage - 1),
    })
  }

  if (currentActivePage === totalPages - 1) {
    alwaysVisiblePages.push(
      {
        pageNumber: currentActivePage - JUMP_TWO_PAGES,
        renderType: AlgoliaPaginationRenderType.NUMBER,
        url: createAlgoliaUrl(currentActivePage - JUMP_TWO_PAGES),
      },
      {
        pageNumber: currentActivePage - 1,
        renderType: AlgoliaPaginationRenderType.NUMBER,
        url: createAlgoliaUrl(currentActivePage - 1),
      },
    )
  }

  if (currentActivePage + 1 === totalPages - 1) {
    alwaysVisiblePages.push({
      pageNumber: currentActivePage - 1,
      renderType: AlgoliaPaginationRenderType.NUMBER,
      url: createAlgoliaUrl(currentActivePage - 1),
    })
  }

  return alwaysVisiblePages
}

/*
  There is a case when if the current active page is 2 or 3 then we need to show all pages from 1 up until 3.
  E.g. We have 25 pages, we are on page 3 (current active page). This means we will have to build: <, 1, 2
  Then in the parent function we will also add 3 to the array, and we will have: <, 1, 2, 3
 */
const buildAllLeftSidePages = (
  currentActivePage: number,
  createAlgoliaUrl: (page: number) => string,
): PaginationItem[] => {
  const leftSidePaginationItems = [
    buildLeftArrowPaginationItem(currentActivePage, createAlgoliaUrl),
  ]

  for (let page = 0; page < currentActivePage; page += 1) {
    const paginationItem = {
      pageNumber: page,
      renderType: AlgoliaPaginationRenderType.NUMBER,
      url: createAlgoliaUrl(page),
    }

    leftSidePaginationItems.push(paginationItem)
  }

  return leftSidePaginationItems
}

/*
  We build all the pagination items that are on the left side of the Current Active Page.
  This includes "...", numbers and "<"
  E.g. We have 25 pages, current active page is 6. This means we build: <, 1, ...,
 */
const buildLeftSidePaginationItems = (
  algoliaPagination: PaginationRenderState,
): PaginationItem[] => {
  const {
    createURL: createAlgoliaUrl,
    currentRefinement: currentActivePage,
    nbPages: totalPages,
  } = algoliaPagination

  if (totalPages <= ALWAYS_SHOW_PAGES_IF_THERE_ARE_FEW_PAGES) {
    return buildAllLeftSidePages(currentActivePage, createAlgoliaUrl)
  }

  const leftSidePaginationItems = []

  const alwaysVisiblePageItems = buildAlwaysVisibleLeftSidePageItems(
    currentActivePage,
    createAlgoliaUrl,
    totalPages,
  )

  leftSidePaginationItems.push(
    buildLeftArrowPaginationItem(currentActivePage, createAlgoliaUrl),
    buildFirstPaginationItem(createAlgoliaUrl),
    ...buildLeftDotsPaginationItem(
      currentActivePage,
      createAlgoliaUrl,
      totalPages,
    ),
    ...alwaysVisiblePageItems,
  )

  return leftSidePaginationItems
}

/*
  The pagination algorithm follows a simple approach of using the Current Active Page as an Anchor for building the other page items.
  We build all the page items from the left side of the current page, then we add the anchor (current active page).
  After the current active page, we end with every page item that should be on the right side it.
  This simple approach gives us clear control of the pagination and should make it easier to maintain in the future.
 */
export const productSearchPaginationBuildPaginationItems = (
  algoliaPagination: PaginationRenderState,
): PaginationItem[] => {
  const {
    createURL: createUrl,
    currentRefinement: currentActivePage,
    isFirstPage,
    isLastPage,
  } = algoliaPagination

  // We build the Active Pagination Item and then decide if we need to build the Left and/or Right side of it
  const activePaginationItem = buildActivePagePaginationItem(
    currentActivePage,
    createUrl,
  )

  // If Anchor Page is the first page then we only build the Right side of the page items
  if (isFirstPage) {
    return [
      activePaginationItem,
      ...buildRightSidePaginationItems(algoliaPagination),
    ]
  }

  // Same logic here but reversed, if the Anchor Page is the last page then we build the Left side of the page items
  if (isLastPage) {
    return [
      ...buildLeftSidePaginationItems(algoliaPagination),
      activePaginationItem,
    ]
  }

  // Here we let each Side handle the amount of page items that need to be built.
  return [
    ...buildLeftSidePaginationItems(algoliaPagination),
    activePaginationItem,
    ...buildRightSidePaginationItems(algoliaPagination),
  ]
}
